import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import type { Trend, TrendFeedResponse, TrendDetail, TrendType } from "@/types/trend.type";
import { trendStats } from "../components/trendMeta";
import {
  axisProduct,
  mergeFanout,
  MAX_FANOUT,
  type FanoutMerge,
  type FanoutPart,
  type WallView,
} from "@/app/routes/dashboard/components/wallFanout";

/**
 * The API also accepts an unnamed default sort, which orders by isBoosted and
 * then by a stored momentumScore. That score is never sent to the client, it is
 * not recomputed, and no honest label exists for it, so the client does not
 * offer it: an ordering the page cannot explain has no place on a page whose
 * whole argument is that it never claims momentum. Boosted trends are already
 * unmistakable on the wall through the double-width plate and the BOOSTED chip.
 *
 * WHICH IS WHY THE WALL NOW SENDS `sort=newest` EXPLICITLY. Leaving `sort` off
 * does not get newest-first, it gets that unnamed default — `feedOrderSql`'s
 * `default:` branch is `t."isBoosted" DESC, t."momentumScore" DESC`. The wall's
 * readout has always said "newest first", so not sending it was the readout
 * describing an order the route was not applying. It also makes the order
 * REPRODUCIBLE on the client, which a merged multi-pick needs: `createdAt` is on
 * every row and `momentumScore` is on none of them, so a merge can only put the
 * rows back in the server's order if that order is one the client can see.
 *
 * EXCEPT WHEN THERE IS A SEARCH. `q` puts the route on its vector path, which orders
 * by cosine distance and IGNORES `sort` outright (TrendRepository.feedBySimilarity).
 * Sending it there would be the same lie in the other direction — a parameter the page
 * asks for and the route drops — so a search sends no `sort` at all, and the client
 * re-sorts a merged search by `similarity`, which the route now returns on every
 * scored row precisely so that order stays reproducible here too.
 */
export type TrendSort = "newest" | "views" | "likes";
export type TrendDateRange = "all" | "day" | "week" | "month";
export type TrendPlatform = "tiktok" | "instagram";

const DATE_RANGE_MS: Record<Exclude<TrendDateRange, "all">, number> = {
  day: 86_400_000,
  week: 7 * 86_400_000,
  month: 30 * 86_400_000,
};

/**
 * The discovery PLANNER fans out across exactly two axes (SIZE_PROFILES.full in
 * the backend's trendConfig.ts): { region: "MENA", language: "ar" } and
 * { region: "global", language: "en" }. Those are the two libraries a run plans
 * against, which is a property of the planner and NOT of the stored column: the
 * distiller writes region and language as free text (pipeline/schemas.ts types
 * them `z.string().nullable()` with no enum), so a row can carry some other
 * value and the client must render whatever it finds.
 *
 * What the planner's pairing does justify is ONE control instead of two: both
 * filters are exact-match server-side, so two independent dropdowns would let a
 * viewer ask for MENA + English, a pair no planned run ever produces and which
 * can therefore only answer zero. This control sends region and language
 * together or not at all, and its first option filters on NEITHER, so a trend
 * tagged outside the two planned libraries is still reachable and still shows
 * its own tag. There is no "GCC" option because no run plans one.
 */
export const TREND_AXES = [
  { key: "all", label: "Any origin", region: undefined, language: undefined },
  { key: "mena-ar", label: "MENA · Arabic", region: "MENA", language: "ar" },
  { key: "global-en", label: "Global · English", region: "global", language: "en" },
] as const satisfies readonly { key: string; label: string; region?: string; language?: string }[];

export type TrendAxisKey = (typeof TREND_AXES)[number]["key"];

export function trendAxis(key: TrendAxisKey): (typeof TREND_AXES)[number] {
  return TREND_AXES.find((a) => a.key === key) ?? TREND_AXES[0];
}

export interface TrendFilters {
  type?: string; platform?: TrendPlatform; q?: string;
  /** Editorial taxonomy the discovery run tagged the trend with ("MENA", "global", ...).
   *  An exact-match hard filter server-side (TrendRepository.buildFeedWhereSql), so the
   *  value has to be one the library actually carries, never a free-text guess. It says
   *  where the EXAMPLE videos were found; it is not a match to any brand. */
  region?: string;
  /** Same contract as `region`: an exact-match hard filter on the trend's own language tag. */
  language?: string;
  minViews?: number; minLikes?: number; dateRange?: TrendDateRange;
  /** An exact ISO instant: only trends the route dates at or after it. The same
   *  `createdAfter` the date-range presets send, set explicitly, which is how the
   *  wall counts what arrived since the viewer last looked. Wins over `dateRange`
   *  when both are set. */
  createdAfter?: string;
  sort?: TrendSort; page?: number; pageSize?: number;
  /** How many example clips each row carries as `previewVideos` (the route
   *  accepts 1 to 6). Only the phone feed asks, so it can swipe between them. */
  previewCount?: number;
}

// Videos are presigned per response, so every refetch mints new URLs. The plates
// are keyed on the trend and the example, not the URL, so a new URL is applied to
// the <video> already on screen rather than building a new one, but it still
// reloads the clip. Trends only change when a discovery run lands, so no
// background polling, and staleTime keeps the global focus-refetch from
// restarting playback.
const TREND_STALE_MS = 5 * 60_000;

const feedKey = (f: TrendFilters) => ["trends", f];

export interface TrendFeedState {
  trends: Trend[];
  total: number;
  totalPages: number;
  nextPage: number | null;
  isLoading: boolean;
  isFetching: boolean;
  /** The rows in hand answer the PREVIOUS question, kept on screen by
   *  `keepPreviousData` while the new one is in flight. Not an answer to what is
   *  being asked, which is why the wall reads it as unread. */
  isPlaceholder: boolean;
  isError: boolean;
  refetch: () => void;
}

/** One filter set, one URL. Extracted so the single-request path and every
 *  constituent of a fan-out build the same query string from the same rules —
 *  two builders would eventually disagree about one parameter and the merged
 *  wall would be answering a slightly different question per request. */
async function fetchTrendFeed(filters: TrendFilters): Promise<TrendFeedResponse> {
  const params = new URLSearchParams();
  params.set("page", String(filters.page ?? 1));
  params.set("pageSize", String(filters.pageSize ?? 24));
  if (filters.type) params.set("type", filters.type);
  if (filters.platform) params.set("platform", filters.platform);
  // Verified against the real route before wiring: v_FeedQuery in
  // TrendController accepts `region` and `language` as plain optional strings
  // and passes them straight to the repository as equality predicates.
  if (filters.region) params.set("region", filters.region);
  if (filters.language) params.set("language", filters.language);
  if (filters.minViews) params.set("minViews", String(filters.minViews));
  if (filters.minLikes) params.set("minLikes", String(filters.minLikes));
  if (filters.createdAfter) {
    params.set("createdAfter", filters.createdAfter);
  } else if (filters.dateRange && filters.dateRange !== "all") {
    params.set("createdAfter", new Date(Date.now() - DATE_RANGE_MS[filters.dateRange]).toISOString());
  }
  if (filters.q) params.set("q", filters.q);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.previewCount && filters.previewCount > 1) {
    params.set("previewCount", String(Math.min(6, filters.previewCount)));
  }
  const res = await axiosInstance.get<TrendFeedResponse>(`/trends?${params.toString()}`);
  return res.data;
}

export function useTrends(filters: TrendFilters, opts?: { enabled?: boolean }): TrendFeedState {
  const query = useQuery({
    queryKey: feedKey(filters),
    enabled: opts?.enabled ?? true,
    queryFn: () => fetchTrendFeed(filters),
    staleTime: TREND_STALE_MS,
    placeholderData: keepPreviousData,
  });
  return {
    trends: query.data?.trends ?? [],
    total: query.data?.total ?? 0,
    totalPages: query.data?.totalPages ?? 1,
    nextPage: query.data?.nextPage ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholder: query.isPlaceholderData,
    isError: query.isError,
    refetch: () => { void query.refetch(); },
  };
}

/* ------------------------------------------------------------- the fan-out */

/**
 * The window ONE fan-out request reads.
 *
 * `v_basePagination` caps `pageSize` at 100 (application/Base/BaseModel.ts), so
 * this is the widest single answer the route can give and the most one picked
 * value can contribute to a merged wall. A picked value whose library runs past
 * it is read short, `truncated` says so, and the merged count is reported as a
 * floor rather than as a total nobody read.
 */
export const TREND_FANOUT_WINDOW = 100;

export interface TrendWallPlan {
  /** One entry per request. Exactly one entry IS the single-request path: the
   *  page the viewer is on, paginated and counted by the route, unchanged from
   *  before any of this existed. */
  requests: TrendFilters[];
  /** The plan was cut at MAX_FANOUT and some combination was never asked. */
  capped: boolean;
}

/**
 * What the wall will ask, given what is picked.
 *
 * Kind and source are both sets and both are exact-match server-side, so the
 * plan is their cross product: `(format OR topic) AND (tiktok OR instagram)` is
 * four whole-library reads whose union is the answer. Four kinds by two sources
 * is 8, which is what MAX_FANOUT is set to, so the cut below is unreachable from
 * today's controls and is here for the day a fifth kind lands.
 *
 * A product of one is the single-request path and keeps the viewer's own page
 * and page size: nothing about a one-value pick changes.
 */
export function trendWallPlan(
  base: Omit<TrendFilters, "type" | "platform" | "page" | "pageSize">,
  types: readonly TrendType[],
  platforms: readonly TrendPlatform[],
  page: number,
  pageSize: number,
): TrendWallPlan {
  const combos = axisProduct(types, platforms);

  if (combos.length === 1) {
    return {
      requests: [
        { ...base, type: combos[0].first, platform: combos[0].second, page, pageSize },
      ],
      capped: false,
    };
  }

  const kept = combos.slice(0, MAX_FANOUT);
  return {
    requests: kept.map((combo) => ({
      ...base,
      type: combo.first,
      platform: combo.second,
      /* Every constituent reads its OWN first window. Their pages cannot be
         lined up with each other, so the merged set is paged on the client and
         these always ask for page one. */
      page: 1,
      pageSize: TREND_FANOUT_WINDOW,
    })),
    capped: kept.length < combos.length,
  };
}

/**
 * The shortest `q` the route answers SEMANTICALLY.
 *
 * `ListTrendsUseCase` embeds the query only at `q.trim().length >= 2` and otherwise
 * falls through to the keyword feed, so one typed character is not a search: it is a
 * title/tag filter in the feed's own order. Mirrored here rather than assumed, because
 * three things on this page hang off the difference — whether `sort` is sent, whether a
 * merged wall re-sorts by relevance, and what the readout names the order — and all
 * three would otherwise be wrong together for exactly one keystroke.
 */
export const TREND_SEMANTIC_MIN_Q = 2;

/** Whether this query reaches the route's vector path at all. */
export function isSemanticQuery(q: string | undefined): boolean {
  return (q?.trim().length ?? 0) >= TREND_SEMANTIC_MIN_Q;
}

const trendId = (trend: Trend) => trend.id;
const trendTime = (trend: Trend) => Date.parse(trend.createdAt);

/**
 * How a merged trends wall has to be ordered, read off the plan that produced it.
 *
 * Derived from the requests rather than passed in beside them, so the order the merge
 * applies cannot drift from the question those requests actually asked: the search
 * score under a search, the picked total under a Most viewed or Most liked sort.
 * `undefined` is the Newest case and leaves the merge on its `createdAt` default.
 *
 * A searched row is always scored, so the `?? 0` is only reachable against a backend
 * older than the field — where sinking unscored rows beats interleaving them into a
 * ranking they were never part of.
 */
export function trendRankOf(
  requests: readonly TrendFilters[],
): ((trend: Trend) => number) | undefined {
  if (requests.some((request) => isSemanticQuery(request.q))) {
    return (trend: Trend) => trend.similarity ?? 0;
  }
  /* A ranked sort is re-applied to the merged rows by the same figure the
     route sorts on (`COALESCE(sum, 0) DESC`), read the way every card reads it:
     a missing total counts as zero. */
  const sort = requests[0]?.sort;
  if (sort === "views") return (trend: Trend) => trendStats(trend).totalViews;
  if (sort === "likes") return (trend: Trend) => trendStats(trend).totalLikes;
  return undefined;
}

/**
 * Merge a fan-out's answers in the order the route would have given one
 * request for the whole question: best match under a search, the picked sort
 * (views or likes, then newest) otherwise, newest first by default. One helper,
 * so the live hook, the fixture harness and the NEW count all merge the same
 * way.
 */
export function trendMerge(
  parts: readonly FanoutPart<Trend>[],
  requests: readonly TrendFilters[],
  capped: boolean,
): FanoutMerge<Trend> {
  const ranked = !requests.some((request) => isSemanticQuery(request.q));
  return mergeFanout(parts, trendId, trendTime, capped, trendRankOf(requests), ranked);
}

/** One answered feed in the shape the merge takes, so the live hook and the dev
 *  fixture harness feed the SAME merge instead of two copies of it. */
export function trendFanoutPart(feed: TrendFeedState, request: TrendFilters): FanoutPart<Trend> {
  const answered = !feed.isLoading && !feed.isError;
  return {
    rows: feed.trends,
    total: answered ? feed.total : null,
    truncated: answered && feed.total > (request.pageSize ?? TREND_FANOUT_WINDOW),
    failed: feed.isError,
    pending: feed.isLoading,
    fetching: feed.isFetching,
    refetch: feed.refetch,
  };
}

/**
 * One request per picked combination, in parallel, merged into one wall.
 *
 * `useQueries` rather than a single query over the whole plan, so each picked
 * value keeps its OWN cache entry: switching {Format, Topic} to {Format, Sound}
 * reuses the Format answer and only reads Sound.
 *
 * No `placeholderData` here, unlike the single-request path. A placeholder
 * inside a merge is one constituent's stale rows sitting beside another's fresh
 * ones — a half-merged wall, which is the one thing the merge is built never to
 * draw. The per-value cache entries are what make re-picking instant instead.
 */
export function useTrendsFanout(
  requests: readonly TrendFilters[],
  opts: { enabled: boolean; capped: boolean },
): FanoutMerge<Trend> {
  const results = useQueries({
    queries: requests.map((filters) => ({
      queryKey: feedKey(filters),
      enabled: opts.enabled,
      queryFn: () => fetchTrendFeed(filters),
      staleTime: TREND_STALE_MS,
    })),
  });

  const parts: FanoutPart<Trend>[] = results.map((result, index) => ({
    rows: result.data?.trends ?? [],
    total: result.data?.total ?? null,
    truncated: result.data
      ? result.data.total > (requests[index].pageSize ?? TREND_FANOUT_WINDOW)
      : false,
    failed: result.isError,
    pending: result.isPending,
    fetching: result.isFetching,
    refetch: () => {
      void result.refetch();
    },
  }));

  return trendMerge(parts, requests, opts.capped);
}

/** The phone feed's rows, read page by page. */
export interface TrendPages {
  /** Every row of the pages answered so far, in order, deduped by id. */
  rows: Trend[];
  /** The route says there is a page after the last one read. */
  hasMore: boolean;
  /** The first page has not answered yet. */
  isLoading: boolean;
  /** A page past the first is still being read. */
  isFetchingMore: boolean;
  /** The first page failed. */
  isError: boolean;
  /** A page after the first failed: the feed has an end it did not choose. */
  moreError: boolean;
  /** Read the failed later page again. */
  retryMore: () => void;
  refetch: () => void;
}

/**
 * Pages 1 to N of ONE request, concatenated: the phone feed asks for the next
 * page as the viewer nears the end, instead of paging a grid. Each page keeps
 * its own cache entry, so going back over a feed never re-reads it. Rows stop
 * at the first page that has not answered, so the feed never shows page three
 * under a gap where page two should be; a later page that fails stops the feed
 * there and says so (`moreError`), with a retry for exactly that page.
 */
export function useTrendPages(requests: readonly TrendFilters[], opts: { enabled: boolean }): TrendPages {
  const results = useQueries({
    queries: requests.map((filters) => ({
      queryKey: feedKey(filters),
      enabled: opts.enabled,
      queryFn: () => fetchTrendFeed(filters),
      staleTime: TREND_STALE_MS,
    })),
  });

  const seen = new Set<string>();
  const rows: Trend[] = [];
  let answered = 0;
  for (const result of results) {
    if (!result.data) break;
    for (const trend of result.data.trends) {
      if (seen.has(trend.id)) continue;
      seen.add(trend.id);
      rows.push(trend);
    }
    answered += 1;
  }
  const lastAnswer = answered > 0 ? results[answered - 1].data : undefined;
  const next = results[answered];

  return {
    rows,
    hasMore: Boolean(lastAnswer && lastAnswer.nextPage !== null) && !next?.isError,
    isLoading: answered === 0 && !(results[0]?.isError ?? false),
    isFetchingMore: answered > 0 && answered < results.length && !next?.isError,
    isError: answered === 0 && (results[0]?.isError ?? false),
    moreError: answered > 0 && Boolean(next?.isError),
    retryMore: () => {
      if (next) void next.refetch();
    },
    refetch: () => {
      for (const result of results) void result.refetch();
    },
  };
}

/** The route's own answer as the wall reads it: it paginated and it counted, so
 *  the figure is a library-wide total and never a floor. */
export function trendSingleView(feed: TrendFeedState): WallView<Trend> {
  return {
    rows: feed.trends,
    pageCount: feed.totalPages,
    count: feed.total,
    atLeast: false,
    /* A PLACEHOLDER IS NOT AN ANSWER, so it reads as loading.

       `keepPreviousData` holds the last question's rows on screen while a new
       search or filter is in flight, and `isLoading` is false throughout because
       the query does hold data - just not data for the question being asked. The
       wall used to draw those rows under `opacity-60`, a dim small enough to
       miss: the viewer searched, the same rows stayed, the same count stayed, and
       nothing on the page said a read was happening. Read as unread instead, and
       every surface already knows what to draw - the skeleton on the wall,
       "reading the library" in the readout and the same line in the live region -
       and the previous question's total can no longer be recorded as the
       library's.

       This is only ever the PREVIOUS question's rows, never the current one's: a
       refetch of the same key leaves `isPlaceholderData` false, so a retry or a
       background re-read still shows the wall it has, dimmed. */
    isLoading: feed.isLoading || feed.isPlaceholder,
    isFetching: feed.isFetching,
    isError: feed.isError,
    failed: 0,
    requested: 1,
    refetch: feed.refetch,
  };
}

/** One trend's detail as a query definition, shared by the panel's hook and by
 *  anything that reads the same answer imperatively (the plate's Save), so both
 *  land on one cache entry and can never ask two different questions. */
export function trendDetailQuery(id: string) {
  return {
    queryKey: ["trend", id] as const,
    queryFn: async () => (await axiosInstance.get<TrendDetail>(`/trends/${id}`)).data,
    staleTime: TREND_STALE_MS,
  };
}

export function useTrendDetail(id: string | null) {
  return useQuery({
    ...trendDetailQuery(id ?? ""),
    enabled: !!id,
  });
}
